// @ts-strict-ignore
import type { NodeState, WorkflowNode, WorkflowNodeType } from 'flow/service/graph';
import type { useSnackBar } from '@usealloy/component-library';
import { IconName } from '@usealloy/component-library';
import {
  isActionAnImportedValue, isActionOfType, isCustomListAction, isUsingActiveListVersion,
} from 'flow/service/actions';
import {
  NODES_SUPPORTING_CONNECTOR,
  NODES_SUPPORTING_MENU,
  NODE_STATES_SUPPORTING_MENU,
  XL_NODES,
} from './constants';
import type { WorkflowState } from '~/dashboard/views/workflows/store/redux/state';
import type { AppDispatch } from '~/store';
import type { DropdownItem } from './NodeDropdownMenu';
import {
  OnDeleteDefaults, OnSwapDefaults, OnUpdateDefaults, OnViewListDefaults,
} from './NodeDropdownMenu';
import type { Action, PublicId } from '~/dashboard/views/workflows/store/engine/types';
import { ActionType } from 'flow/store/engine/constants';
import type { MergeDiffResult } from 'flow/service/changelog/formatters/mergeDropdownFormatter/types';
import { getIsMergeImpacted } from 'flow/service/changelog/formatters/mergeDropdownFormatter/helpers';
import { duplicateNodeThunk } from 'flow/store/redux/thunks';
import getRemoteCurrentAndNextLocalSnippets from 'flow/api/localSnippets/getRemoteCurrentAndNextLocalSnippets';
import { upgradeLinkedSnippetVersionThunk } from 'flow/store/redux/thunks/snippets';
import type { API } from '~/helpers/api-new/modules';
import SnippetVersionBadge from './NodeDropdownMenu/SnippetVersionBadge';

export function isXLNode(nodeType: WorkflowNodeType) {
  return XL_NODES.has(nodeType);
}

export function doesNodeTypeSupportConnector(
  nodeType: WorkflowNodeType,
): boolean {
  return NODES_SUPPORTING_CONNECTOR.includes(nodeType);
}

export function doesNodeTypeSupportMenu(nodeType: WorkflowNodeType): boolean {
  return NODES_SUPPORTING_MENU.includes(nodeType);
}

export function doesNodeStateSupportMenu(nodeState: NodeState): boolean {
  return NODE_STATES_SUPPORTING_MENU.includes(nodeState);
}

export function showNodeMenu(nodeState: NodeState, nodeType: WorkflowNodeType): boolean {
  if (!doesNodeTypeSupportMenu(nodeType)) {
    return false;
  }

  return doesNodeStateSupportMenu(nodeState);
}

export function canDuplicateNode(nodeType: WorkflowNodeType): boolean {
  return nodeType !== 'snippet' && nodeType !== 'riskCalculation';
}


export function doesActionSupportTooltip(action: Action): boolean {
  if (!action) {
    return false;
  }

  return !isActionAnImportedValue(action);
}

export function getAttributeNodeIcon(args: {
  defaultIcon: IconName;
  isPublishedAttribute: boolean;
  objectType?: 'entity' | 'transaction';
  customTransactionAttributesEnabled?: boolean;
}): IconName {
  const {
    defaultIcon,
    isPublishedAttribute,
    objectType,
    customTransactionAttributesEnabled,
  } = args;

  // Default behavior for getAttributeNodeIcon when feature flag is not set
  if (!customTransactionAttributesEnabled) {
    const iconName = isPublishedAttribute ? IconName.Bookmark : defaultIcon;
    return iconName;
  }

  // Feature flag behavior: Determine icon based on published attribute type
  if (isPublishedAttribute) {
    // Only differentiate by object type if feature flag is enabled
    if (objectType === 'transaction') {
      return IconName.MoneyBracket; // Transaction icon
    }
    return IconName.Person; // Entity icon (default for published attributes)
  }

  return defaultIcon; // Default for non-published attributes
}

export function getDropdownItems({
  addSnackBar,
  canDuplicate,
  closeRuleTooltip,
  dispatch,
  libraryApi,
  nodeId,
  nodeType,
  onCopy,
  onUpdateAction,
  setNodeDeletionChildActionCounts,
  typhonDomain,
  workflowData,
  workflowProps,
}: {
  addSnackBar: ReturnType<typeof useSnackBar>;
  canDuplicate: boolean;
  closeRuleTooltip: () => void;
  dispatch: AppDispatch;
  libraryApi: API['library'];
  nodeId: WorkflowNode['id'];
  nodeType: WorkflowNodeType;
  onCopy?: (arg?: WorkflowNode['id']) => void;
  onUpdateAction: React.Dispatch<React.SetStateAction<boolean>>;
  setNodeDeletionChildActionCounts: React.Dispatch<React.SetStateAction<number>>;
  typhonDomain: string;
  workflowData: WorkflowState['workflowData'];
  workflowProps: WorkflowState['workflowProps'];
}): DropdownItem[] {
  const action = workflowData?.actions?.[nodeId];
  const customLists = workflowProps?.customLists;

  const isCustomListService = isCustomListAction(action);
  const isAlreadyActive = isCustomListService && isUsingActiveListVersion(action, customLists);


  let nextLinkedSnippetVersion = null;

  // include existence check for action.snippet
  // sometimes the action data is removed before the node component is unmounted in time
  const isSnippet = isActionOfType(action, ActionType.SNIPPET) && Boolean(action.snippet);
  if (isSnippet) {
    const snippetToken = action.snippet?.token;
    // it's safe to read it from workflowProps
    // as we can only ever upgrade to the latest published version,
    nextLinkedSnippetVersion = workflowProps?.snippets[snippetToken]?.published_version;
  }

  const dropdownItems: DropdownItem[] = [
    ...(onCopy ? [{
      label: 'Copy',
      onSelect: () => onCopy(nodeId),
      Icon: null,
      style: null,
    }] : []),
    ...(canDuplicate ? [{
      label: 'Duplicate',
      onSelect: () => {
        dispatch(duplicateNodeThunk(nodeId));
        closeRuleTooltip();
      },
      Icon: null,
      style: null,
      dataId: 'alloydd-workflow-duplicate-node',
    }] : []),
    ...(nodeType === ActionType.TAG ? [{
      label: OnSwapDefaults.label,
      onSelect: OnSwapDefaults.onSelect({
        dispatch,
        nodeId,
      }),
      Icon: null,
      style: null,
    }] : []),
    ...(isCustomListService ? [{
      label: OnViewListDefaults.label,
      onSelect: OnViewListDefaults.onSelect({
        nodeId,
        workflowData,
      }),
      Icon: OnViewListDefaults.Icon,
      style: OnViewListDefaults.style,
    }, {
      label: OnUpdateDefaults.label,
      onSelect: OnUpdateDefaults.onSelect({
        onUpdateAction,
      }),
      Icon: null,
      style: null,
      disabled: isAlreadyActive,
    }] : []),
    ...(isSnippet ? [
      {
        label: 'Detach from Library',
        // TODO: Implement detach from library
        onSelect: () => {},
        Icon: null,
        style: null,
      },
      {
        disabled: action.snippet.publicVersion === nextLinkedSnippetVersion,
        label: 'Upgrade to latest published',
        onSelect: async () => {
          try {
            const {
              remoteCurrentSnippet,
              remoteNextSnippet,
            } = await getRemoteCurrentAndNextLocalSnippets({
              currentVersion: action.snippet.publicVersion,
              libraryApi,
              nextVersion: nextLinkedSnippetVersion,
              snippetToken: action.snippet.token,
              typhonDomain,
              workflowProps,
            });

            await dispatch(upgradeLinkedSnippetVersionThunk({
              currentRemoteSnippet: remoteCurrentSnippet,
              localSnippetActionPublicId: nodeId,
              nextRemoteSnippet: remoteNextSnippet,
            }));
          } catch (err) {
            addSnackBar({
              title: 'Error upgrading snippet version',
              type: 'danger',
              duration: 5_000,
            });
          }
        },
        // we call the component's function instead of using JSX as this file is TS only
        Icon: SnippetVersionBadge({ versionNumber: nextLinkedSnippetVersion }),
        style: {
          display: 'flex',
          flexDirection: 'row-reverse',
        },
      } as DropdownItem,
    ] : []),
    {
      label: OnDeleteDefaults.label,
      onSelect: OnDeleteDefaults.onSelect({
        addSnackBar,
        dispatch,
        nodeId,
        workflowData,
        setNodeDeletionChildActionCounts,
      }),
      Icon: OnDeleteDefaults.Icon,
      style: OnDeleteDefaults.style,
    },
  ];

  return dropdownItems;
}

/**
  * Disallow hover treatment if we are merging and this is not a merge
  * impacted node. Allow otherwise.
  */
export function getCanHoverOnNode(mergeDiff: MergeDiffResult | null, nodeId: PublicId) {
  return !mergeDiff || (mergeDiff && getIsMergeImpacted(mergeDiff, nodeId));
}
